import React, { useState } from 'react';
import { RotateCcw, CheckCircle2, AlertTriangle, Database } from 'lucide-react';

const RollbackFramework = ({ project }) => {
  const [framework] = useState({
    rollback_points: [
      { checkpoint: 'Pre-SUM System State', timestamp: '2024-01-15 00:00', size_gb: 2450, restore_time_min: 180, status: 'available' },
      { checkpoint: 'Post-SPAU System State', timestamp: '2024-01-15 06:30', size_gb: 2480, restore_time_min: 185, status: 'available' },
      { checkpoint: 'Pre-DMO System State', timestamp: '2024-01-15 08:00', size_gb: 2480, restore_time_min: 185, status: 'available' }
    ],
    rollback_procedures: [
      { step: 1, action: 'Stop S/4HANA system', duration_min: 5, automation: 'Full' },
      { step: 2, action: 'Restore database from checkpoint', duration_min: 180, automation: 'Full' },
      { step: 3, action: 'Verify data consistency', duration_min: 30, automation: 'Partial' },
      { step: 4, action: 'Restart ECC system', duration_min: 15, automation: 'Full' },
      { step: 5, action: 'Validate business processes', duration_min: 60, automation: 'Manual' }
    ],
    estimated_rollback_time_hours: 4.8,
    automation_percentage: 75
  });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-3xl font-bold mb-2">Rollback Automation Framework</h2>
        <p className="text-gray-600">Automated rollback procedures with system restore points</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-blue-500 rounded-xl p-6 text-white">
          <Database className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{framework.rollback_points.length}</div>
          <div className="text-sm">Rollback Points</div>
        </div>
        <div className="bg-orange-500 rounded-xl p-6 text-white">
          <RotateCcw className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{framework.estimated_rollback_time_hours}h</div>
          <div className="text-sm">Rollback Time</div>
        </div>
        <div className="bg-green-500 rounded-xl p-6 text-white">
          <CheckCircle2 className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{framework.automation_percentage}%</div>
          <div className="text-sm">Automated</div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">System Restore Points</h3>
        <div className="space-y-3">
          {framework.rollback_points.map((point, i) => (
            <div key={i} className="border-2 border-green-200 bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{point.checkpoint}</h4>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">{point.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Timestamp</p>
                  <p className="font-semibold">{new Date(point.timestamp).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-500">Size</p>
                  <p className="font-semibold">{point.size_gb} GB</p>
                </div>
                <div>
                  <p className="text-gray-500">Restore Time</p>
                  <p className="font-semibold">{point.restore_time_min} min</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">Rollback Procedure</h3>
        <div className="space-y-3">
          {framework.rollback_procedures.map((proc) => (
            <div key={proc.step} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">{proc.step}</div>
              <div className="flex-1">
                <h4 className="font-semibold">{proc.action}</h4>
                <p className="text-sm text-gray-600">{proc.duration_min} min | {proc.automation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RollbackFramework;